import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardActions, CardContent } from '@mui/material'
import AlumnoHeader from '../../../components/AlumnoHeader/AlumnoHeader'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import { getAlumno, updateFotoAlumno } from '../../../utils/api/alumnos'
import { makeUniqueUrl } from '../../../utils/request'
import defaultImage from './profile.jpg'
import useAlumno from '../../../hooks/useAlumno'
import css from './Foto.module.css'

const Foto = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()
  const { id: alumnoId } = useAlumno()

  const [fotoCamara, setFotoCamara] = useState({ file: null, preview: '' })
  const videoRef = useRef(null)

  const { data: fotoUrl = '' } = useQuery({
    queryKey: ['alumnos', alumnoId, 'foto-url'],
    enabled: !!alumnoId,
    queryFn: () =>
      getAlumno(alumnoId)
        .then(datos => makeUniqueUrl(datos.fotoUrl))
        .catch(() => '')
  })

  const { isPending: isFotoPending, mutate: updateFoto } = useMutation({
    mutationFn: updateFotoAlumno,
    onSuccess: () => {
      notification.success({
        title: 'Foto',
        content: 'Foto subida correctamente'
      })
      queryClient.invalidateQueries({
        queryKey: ['alumnos', alumnoId]
      })
      if (fotoCamara.preview) URL.revokeObjectURL(fotoCamara.preview)
      setFotoCamara({ file: null, preview: '' })
    },
    onError: err => {
      notification.error({ title: 'Error subiendo la foto', content: err })
    }
  })

  const handleUploadFoto = () => {
    if (isFotoPending) return
    updateFoto({ foto: fotoCamara.file, alumno: alumnoId })
  }

  const handleTakePicture = () => {
    const photo = document.createElement('canvas')
    const video = videoRef.current
    photo.height = 150
    photo.width = (video.videoWidth * photo.height) / video.videoHeight
    const ctx = photo.getContext('2d')
    ctx.drawImage(video, 0, 0, photo.width, photo.height)
    photo.toBlob(blob => {
      if (fotoCamara.preview) URL.revokeObjectURL(fotoCamara.preview)
      setFotoCamara({ file: blob, preview: URL.createObjectURL(blob) })
      photo.remove()
    })
  }

  const handleClearPicture = () => {
    if (fotoCamara.preview) URL.revokeObjectURL(fotoCamara.preview)
    setFotoCamara({ file: null, preview: '' })
  }
  useEffect(() => {
    let stream
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(s => {
        stream = s
        const video = videoRef.current
        video.srcObject = stream
        video.onloadedmetadata = () => video.play()
      })
      .catch(() => {
        notification.error({
          title: 'Error de permisos de webcam',
          content: 'No se ha podido acceder a la webcam. Revisa los permisos'
        })
      })
    return () => {
      if (stream) stream.getTracks().forEach(track => track.stop())
    }
  }, [notification])

  useEffect(() => {
    return () => {
      if (fotoCamara.preview) URL.revokeObjectURL(fotoCamara.preview)
    }
  }, [fotoCamara.preview])

  return (
    <div className={css.main}>
      <AlumnoHeader label='Foto' />
      <div className={css.content}>
        <div className={css.cards}>
          <Card className={css.card} variant='elevation'>
            <CardContent>
              <img
                className={css.image}
                data-visible={fotoCamara.file ? 'visible' : 'hidden'}
                src={fotoCamara.preview}
                alt='foto alumno'
              />
              <video
                className={css.video}
                data-visible={fotoCamara.file ? 'hidden' : 'visible'}
                ref={videoRef}
                muted
              />
            </CardContent>
            <CardActions className={css.actions}>
              {!fotoCamara.file && (
                <Button
                  size='small'
                  variant='outlined'
                  onClick={handleTakePicture}
                >
                  Hacer foto
                </Button>
              )}
              {fotoCamara.file && (
                <>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={handleUploadFoto}
                    disabled={!fotoCamara.file}
                  >
                    Subir foto
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    color='warning'
                    onClick={handleClearPicture}
                  >
                    Sacar otra foto
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
          <Card className={css.card} variant='elevation'>
            <CardContent>
              <img
                src={fotoUrl || defaultImage}
                alt='foto alumno'
                className={css.image}
              />
            </CardContent>
            <CardActions className={css.actions}>
              {fotoUrl ? 'Foto actual' : 'Sin foto actual'}
            </CardActions>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Foto
